import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import "@/theme-orange.css";
import "@/theme-skins.css";
import "@/theme-layouts.css";
import "@/theme-login.css";
import App from "@/App";
import { runVersionCheck } from "@/lib/versionCheck";

// On every page load, check that the browser is running the latest build.
// This covers the plain-web (non-PWA) case where stale JS/CSS may be cached
// by the browser or CDN. Auth data (localStorage / IndexedDB / cookie) is
// preserved — only the Cache Storage API is purged.
runVersionCheck();

// Also re-check when the tab becomes visible again after being backgrounded,
// so a browser tab left open across a deploy will refresh on next glance.
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") runVersionCheck();
  });
}

// --------------------------------------------------------------------------
// Disable browser pinch-to-zoom / double-tap-zoom on the whole PWA.
// The Estimate workspace implements its own pinch handler (touchstart/
// touchmove) so it still works there — we only kill the native gesture.
// iOS Safari also fires gesturestart events, which are blocked here.
// Ctrl/Cmd+wheel desktop zoom is INTENTIONALLY left enabled so users can
// zoom the whole app with their scroll wheel like any normal webpage.
// --------------------------------------------------------------------------
if (typeof document !== "undefined") {
  const insideEstimateViewer = (el) => !!el?.closest?.('[data-testid="estimate-file-viewer"]');
  document.addEventListener("gesturestart", (e) => { if (!insideEstimateViewer(e.target)) e.preventDefault(); });
  document.addEventListener("gesturechange", (e) => { if (!insideEstimateViewer(e.target)) e.preventDefault(); });
  document.addEventListener("gestureend", (e) => { if (!insideEstimateViewer(e.target)) e.preventDefault(); });
}


// Register the Elite PWA service worker (and clean up any legacy SWs like the
// previously-deployed "PlanFlow" app that may still be intercepting traffic).
// Auto-update flow:
//   - register with updateViaCache: 'none' so /sw.js is never HTTP-cached
//   - on load AND on tab-focus, call reg.update() to check for new bytes
//   - when a new SW enters 'installed' state, prompt it to skipWaiting
//   - on controllerchange, reload the window ONCE so the new SW's assets are
//     picked up (a boolean guard prevents reload loops during dev)
if (typeof window !== "undefined" && "serviceWorker" in navigator) {
  let reloadingForNewSW = false;
  const doReload = () => {
    if (reloadingForNewSW) return;
    reloadingForNewSW = true;
    window.location.reload();
  };
  navigator.serviceWorker.addEventListener("controllerchange", doReload);

  const watchRegistration = (reg) => {
    if (!reg) return;
    // If a new SW is already waiting (found before our listener attached),
    // tell it to activate immediately.
    if (reg.waiting && navigator.serviceWorker.controller) {
      reg.waiting.postMessage({ type: "SKIP_WAITING" });
    }
    reg.addEventListener("updatefound", () => {
      const nw = reg.installing;
      if (!nw) return;
      nw.addEventListener("statechange", () => {
        if (nw.state === "installed" && navigator.serviceWorker.controller) {
          // New version ready — tell it to take over now.
          nw.postMessage({ type: "SKIP_WAITING" });
        }
      });
    });
    // Periodic + focus-based update checks (foreground only).
    const check = () => { try { reg.update(); } catch (e) { /* offline */ } };
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") check();
    });
    setInterval(check, 15 * 60 * 1000); // every 15 min while open
  };

  window.addEventListener("load", async () => {
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      for (const r of regs) {
        const url = r.active?.scriptURL || r.installing?.scriptURL || "";
        // Unregister anything that isn't our /sw.js so it can't hijack navigations.
        if (!url.endsWith("/sw.js")) {
          try { await r.unregister(); } catch (e) { /* ignore */ }
        }
      }
      if (window.caches && caches.keys) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch (e) { /* ignore */ }
    try {
      const reg = await navigator.serviceWorker.register("/sw.js", { updateViaCache: "none" });
      watchRegistration(reg);
    } catch (e) { /* ignore */ }
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
